import { Tooltip } from 'antd';
import {
	ArrowRightOutlined,
	CheckOutlined,
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { useRef, useState } from 'react';
import RuleRow, { ReadChip } from './RuleRow';
import {
	addRule,
	clearDefault,
	hasDefault,
	moveRule,
	readValueSpec,
	removeRule,
	setDefault,
	updateRule,
} from 'src/_shared/services/job-sync/serde';

// THE reusable centerpiece. Fully controlled by a raw `RuleSet` (`value`) + `onChange`; it knows
// nothing about JobSyncConfig, bonuses, or persistence. Specialization is injected:
//   - `renderResult(spec, onSpecChange)` renders the result EDIT control (a bonus name, a value spec, …)
//   - `renderResultRead(spec)` renders the read-view result text (defaults to the ValueSpec shapes)
//   - `outLabel` is the connective before the result while editing ("set", "bonus", …)
//   - `fieldOptions` seed the field AutoComplete
//   - `truncateAt` is how many value chips a condition shows before collapsing to a "+N more" pill
// Rules render as read-only sentences (the design's read view); a row's pencil opens the inline
// editor, and rows added here start in edit mode. Ordered rules are first-match-wins; the
// `otherwise` default is a separate, optional, always-last row.
//
// Stable React keys come from an id sidecar held in a ref (never serialized): the structural handlers
// (add / remove / move) mutate ids in lockstep with the rules, so a reorder moves its row's identity
// with it and per-row state (focus, caret) doesn't jump to a neighbor.
export default function RuleBuilder({
	title,
	icon,
	value,
	onChange,
	outLabel = 'set',
	fieldOptions = [],
	renderResult,
	renderResultRead,
	truncateAt = 1,
	note,
}) {
	const rules = value.rules || [];
	const idsRef = useRef(null);
	const seqRef = useRef(0);
	const lastAddedRef = useRef(null);
	const [editingDefault, setEditingDefault] = useState(false);
	const nextId = () => {
		seqRef.current += 1;
		return seqRef.current;
	};

	if (idsRef.current === null) {
		idsRef.current = rules.map(() => nextId());
	}
	if (idsRef.current.length !== rules.length) {
		const cur = idsRef.current;
		idsRef.current = rules.map((_, i) => (cur[i] !== undefined ? cur[i] : nextId()));
	}
	const ids = idsRef.current;

	const handleAdd = () => {
		const id = nextId();
		lastAddedRef.current = id;
		idsRef.current = [...ids, id];
		onChange(addRule(value));
	};
	const handleRemove = (i) => {
		idsRef.current = ids.filter((_, k) => k !== i);
		onChange(removeRule(value, i));
	};
	const handleMove = (from, to) => {
		if (to < 0 || to >= rules.length) return;
		const arr = ids.slice();
		const [moved] = arr.splice(from, 1);
		arr.splice(to, 0, moved);
		idsRef.current = arr;
		onChange(moveRule(value, from, to));
	};

	// Read-view result for plain ValueSpecs: literal → bold text; copyFrom → "use field" + chip;
	// template → "template" + chip. Specializations (bonus amounts) inject their own via prop.
	const defaultResultRead = (spec) => {
		const { kind, value: v } = readValueSpec(spec);
		const empty = v == null || v === '';
		if (kind === 'copyFrom') {
			return (
				<>
					<span className="js-kw">use field</span>
					<ReadChip value={v} placeholder="field" accent />
				</>
			);
		}
		if (kind === 'template') {
			return (
				<>
					<span className="js-kw">template</span>
					{/* eslint-disable-next-line no-template-curly-in-string */}
					<ReadChip value={v} placeholder="${field}" accent />
				</>
			);
		}
		return empty ? <span className="js-kw">not set</span> : String(v);
	};
	const resultRead = renderResultRead || defaultResultRead;

	return (
		<div className="js-card">
			<div className="js-card-head">
				<div className="js-card-title">
					{icon ? <span className="js-ico">{icon}</span> : null}
					{title}
				</div>
				<span className="js-badge">first match wins</span>
			</div>

			<div className="js-rules">
				{rules.map((rule, i) => (
					<RuleRow
						key={ids[i]}
						rule={rule}
						index={i}
						total={rules.length}
						fieldOptions={fieldOptions}
						outLabel={outLabel}
						renderResult={renderResult}
						renderResultRead={resultRead}
						truncateAt={truncateAt}
						defaultEditing={ids[i] === lastAddedRef.current}
						onChange={(nextRule) => onChange(updateRule(value, i, nextRule))}
						onRemove={() => handleRemove(i)}
						onMoveUp={() => handleMove(i, i - 1)}
						onMoveDown={() => handleMove(i, i + 1)}
					/>
				))}

				{rules.length === 0 && (
					<div className="js-muted" style={{ padding: '4px 0 2px' }}>
						No rules yet — every job falls straight to the default below.
					</div>
				)}

				{hasDefault(value) ? (
					<div className={`js-rule js-default${editingDefault ? ' is-editing' : ''}`}>
						<div className="js-rule-body">
							<span className="js-kw">Otherwise</span>
							<span className="js-arrow" aria-hidden>
								<ArrowRightOutlined />
							</span>
							{editingDefault ? (
								<>
									<span className="js-kw">{outLabel}</span>
									<div className="js-token js-result js-token-val">
										{renderResult(value.default, (spec) => onChange(setDefault(value, spec)))}
									</div>
								</>
							) : (
								<span className="js-res-text">{resultRead(value.default)}</span>
							)}
							<div className="js-rule-tail">
								<Tooltip title={editingDefault ? 'Done' : 'Edit default'}>
									<button
										type="button"
										className={`js-iconbtn${editingDefault ? ' is-on' : ''}`}
										onClick={() => setEditingDefault(!editingDefault)}
										aria-label={editingDefault ? 'Done editing default' : 'Edit default'}
									>
										{editingDefault ? <CheckOutlined /> : <EditOutlined />}
									</button>
								</Tooltip>
								<Tooltip title="Remove default">
									<button
										type="button"
										className="js-iconbtn js-danger"
										onClick={() => {
											setEditingDefault(false);
											onChange(clearDefault(value));
										}}
										aria-label="Remove default"
									>
										<DeleteOutlined />
									</button>
								</Tooltip>
							</div>
						</div>
					</div>
				) : null}
			</div>

			<div className="js-card-foot">
				<div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
					<button type="button" className="js-linkbtn" onClick={handleAdd}>
						<PlusOutlined /> Add rule
					</button>
					{!hasDefault(value) && (
						<button
							type="button"
							className="js-linkbtn"
							onClick={() => {
								setEditingDefault(true);
								onChange(setDefault(value, { literal: '' }));
							}}
						>
							<PlusOutlined /> Add default
						</button>
					)}
				</div>
				{note ? <span className="js-muted">{note}</span> : null}
			</div>
		</div>
	);
}
